import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import * as yup from 'yup';

import Button, { BUTTON_STATES } from 'components/shared/button';
import Link from 'components/shared/link';
import DEFAULT_EASE from 'constants/default-ease';
import { BUTTON_SUCCESS_TIMEOUT_MS, REQUEST_DEMO_FORM_ID, URL_PARAMS } from 'constants/forms';
import useSegmentAnonId from 'hooks/use-segment-anon-id';
import SendIcon from 'icons/send.inline.svg';
import getParamsValue from 'utils/get-params-value';
import mutinyIdentify from 'utils/mutiny-identification';
import sendGravityFormData from 'utils/send-gravity-form-data';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .email('Please provide a valid email')
    .required('Email is a required field'),
});

const RequestDemo = (props) => {
  const {
    className,
    title,
    customTitle,
    description,
    inputPlaceholder,
    buttonText,
    buttonUrl,
    customButtonText,
    customButtonUrl,
    linkText,
    linkUrl,
    customLinkText,
    customLinkUrl,
    caseStudyText,
    caseStudyAuthorImage,
    caseStudyAuthorName,
    caseStudyLinkText,
    caseStudyLinkUrl,
  } = props;

  const [quoteRef, isQuoteInView] = useInView({ triggerOnce: true, threshold: 0.8 });
  const [buttonState, setButtonState] = useState(BUTTON_STATES.DEFAULT);
  const { getSegmentAnonId, SegmentAnonIdInput } = useSegmentAnonId();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) });

  const onSubmit = async (values) => {
    const { email } = values;
    const urlParams = getParamsValue(URL_PARAMS);
    const segmentAnonIdC = getSegmentAnonId();
    setButtonState(BUTTON_STATES.LOADING);

    mutinyIdentify({ email });

    try {
      await sendGravityFormData(REQUEST_DEMO_FORM_ID, {
        input_1: email,
        input_2: urlParams.utm_source,
        input_3: urlParams.utm_medium,
        input_4: urlParams.utm_campaign,
        input_5: urlParams.utm_term,
        input_6: urlParams.utm_content,
        input_7: urlParams.gclid,
        input_8: segmentAnonIdC,
      });

      setButtonState(BUTTON_STATES.SUCCESS);

      setTimeout(() => {
        setButtonState(BUTTON_STATES.DEFAULT);
        reset();
      }, BUTTON_SUCCESS_TIMEOUT_MS);
    } catch (error) {
      setButtonState(BUTTON_STATES.DEFAULT);
    }
  };

  return (
    <section className={clsx('safe-paddings bg-black py-20 text-white md:py-14', className)}>
      <div className="container grid-gap-x grid grid-cols-12 items-center lg:flex lg:flex-col lg:items-center">
        <div className="col-span-6 lg:w-full lg:max-w-[585px]">
          <h2
            className={clsx('text-5xl font-bold leading-denser text-white', {
              'xs:text-center': buttonUrl,
            })}
          >
            {customTitle || title}
          </h2>
          {description && <p className="mt-3">{description}</p>}
          {buttonUrl && (
            <div className="mt-6 flex items-center space-x-7 xs:flex-col xs:space-x-0 xs:space-y-5">
              <Button to={customButtonUrl || buttonUrl} size="md" theme="secondary-2">
                {customButtonText || buttonText}
              </Button>
              {((customLinkUrl && customLinkText) || (linkText && linkUrl)) && (
                <Link
                  className="whitespace-nowrap"
                  to={customLinkUrl || linkUrl}
                  size="sm"
                  theme="white-underline"
                >
                  {customLinkText || linkText}
                </Link>
              )}
            </div>
          )}

          {!buttonUrl && (
            <form
              className="relative mt-10 xl:mt-6 lg:mt-7"
              method="post"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="relative">
                <input
                  className="remove-autocomplete-styles block h-20 w-full appearance-none rounded-full bg-white px-8 pr-60 text-lg leading-none text-black placeholder-black outline-none transition-colors duration-200 placeholder:overflow-visible xl:h-16 xl:pr-56 md:pr-18 md:text-[16px] xs:pl-4"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={inputPlaceholder}
                  {...register('email')}
                />
                {errors?.email?.message && (
                  <span className="absolute -bottom-2 left-0 w-full translate-y-full text-center text-sm leading-none text-primary-2 sm:text-xs">
                    {errors.email.message}
                  </span>
                )}
              </div>
              <SegmentAnonIdInput />
              <Button
                className="absolute right-3 top-1/2 -translate-y-1/2 md:right-1.5 md:h-[52px] md:pl-3.5 md:pr-2.5"
                size="md"
                theme="secondary-2"
                state={buttonState}
              >
                <span className="md:hidden">{buttonText}</span>
                <SendIcon className="hidden w-7 md:block" />
              </Button>
            </form>
          )}
        </div>

        <figure
          className="relative col-span-5 col-start-8 overflow-hidden rounded-[30px] border border-gray-9 p-8 xl:p-7 lg:mt-14 lg:max-w-[585px] md:mt-10 md:w-full md:px-5 md:py-7 xs:px-5 xs:py-6"
          ref={quoteRef}
        >
          <blockquote>
            <p className="text-2xl">“{caseStudyText}”</p>
          </blockquote>
          <figcaption className="mt-5 flex items-center space-x-4 xl:space-x-3.5 md:space-x-2">
            <img
              className="w-12 shrink-0 rounded-full xs:w-10"
              src={caseStudyAuthorImage.url}
              height={48}
              width={48}
              loading="lazy"
              alt={caseStudyAuthorName}
            />
            <span className="text-base font-medium">{caseStudyAuthorName}</span>
          </figcaption>
          <Link className="mt-8 xl:mt-5" to={caseStudyLinkUrl} size="sm" theme="white-underline">
            {caseStudyLinkText}
          </Link>
          <svg
            className="absolute bottom-0 right-0 w-40 xl:-bottom-3.5 xl:-right-3.5 sm:-bottom-5 sm:-right-6 xs:hidden"
            viewBox="0 0 160 184"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <LazyMotion features={domAnimation}>
              <m.path
                d="M 1 185 C 1 185 37.7054 78 83 135 C 128.295 192 106 1 161 1"
                stroke="#f25a5a"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={isQuoteInView && { pathLength: 1 }}
                transition={{ duration: 2, ease: DEFAULT_EASE }}
              />
            </LazyMotion>
          </svg>
        </figure>
      </div>
    </section>
  );
};

RequestDemo.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  customTitle: PropTypes.string,
  description: PropTypes.string,
  inputPlaceholder: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonUrl: PropTypes.string,
  customButtonText: PropTypes.string,
  customButtonUrl: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  customLinkText: PropTypes.string,
  customLinkUrl: PropTypes.string,
  caseStudyText: PropTypes.string.isRequired,
  caseStudyAuthorImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
  caseStudyAuthorName: PropTypes.string.isRequired,
  caseStudyLinkText: PropTypes.string.isRequired,
  caseStudyLinkUrl: PropTypes.string.isRequired,
};

RequestDemo.defaultProps = {
  className: null,
  customTitle: null,
  description: null,
  buttonUrl: null,
  customButtonText: null,
  customButtonUrl: null,
  linkText: null,
  linkUrl: null,
  customLinkText: null,
  customLinkUrl: null,
};

export default RequestDemo;
